import { Chip, Typography, ButtonGroup, Button } from '@mui/material';
import { map } from 'lodash';
import TabelaBase from './tabelaBase';
import { NotaBMF, NotaBovespa } from '../../../models/nota';
import ChipCorretora from '../ui/chipCorretora';
import { SituacaoNota } from '../../../models/enum';
import { IconFileSearch, IconRepeat, IconTrashX } from '@tabler/icons-react';
import NotaDb from '../../../db/notaDb';
import { deleteDoc } from 'firebase/firestore';
import ChipNegociacao from '../ui/chipNegociacao';
import Functions from '../../../callable/functions';
import { Link, useNavigate } from 'react-router-dom';
import MoedaText from '../ui/inputs/moedaText';
import InteiroText from '../ui/inputs/inteiroText';

type Props = { notas: (NotaBovespa | NotaBMF)[]; loading?: boolean };

export default function TabelaNotas({ notas, loading = false }: Props) {
  const navigate = useNavigate();
  const header: {
    titulo: string;
    key: string;
    ordem: number;
    align: 'left' | 'center' | 'right';
  }[] = [
    { titulo: 'Dia', key: 'dia', ordem: 1, align: 'center' },
    { titulo: 'Corretora', key: 'corretora', ordem: 2, align: 'center' },
    { titulo: 'Negociação', key: 'tipoNegociacao', ordem: 3, align: 'center' },
    { titulo: 'Operações', key: 'operacoes', ordem: 4, align: 'right' },
    { titulo: 'Valor Líquido', key: 'valorLiquido', ordem: 5, align: 'right' },
    { titulo: 'Situacao', key: 'situacao', ordem: 6, align: 'center' },
    { titulo: 'Upload', key: 'upload', ordem: 7, align: 'center' },
    { titulo: '', key: 'acoes', ordem: 8, align: 'right' },
  ];
  const rows = map(notas, (nota) => ({
    dia: celulaDia(nota),
    operacoes: celulaOperacoes(nota),
    valorLiquido: celulaValorLiquido(nota),
    corretora: celulaCorretora(nota),
    situacao: celulaSituacao(nota),
    upload: celulaUpload(nota),
    tipoNegociacao: celulaTipoNegociacao(nota),
    acoes: celulaAcoes(nota),
  }));

  return <TabelaBase header={header} rows={rows} loading={loading} />;

  function celulaAcoes(nota: NotaBovespa | NotaBMF): JSX.Element {
    return (
      <ButtonGroup size="small" variant="outlined">
        <Button
          size="small"
          color="info"
          onClick={() => navigate(`/arquivo/nota/${nota.id}`)}
        >
          <IconFileSearch size={16} />
        </Button>
        <Button
          size="small"
          color="info"
          onClick={() => {
            new Functions().prepararNotaManual({ notaId: nota.id });
          }}
        >
          <IconRepeat size={16} />
        </Button>
        <Button
          size="small"
          color="error"
          onClick={() =>
            deleteDoc(
              new NotaDb().doc({ usuarioId: nota.usuario, notaId: nota.id }),
            )
          }
        >
          <IconTrashX size={16} />
        </Button>
      </ButtonGroup>
    );
  }

  function celulaTipoNegociacao(nota: NotaBovespa | NotaBMF): JSX.Element {
    return <ChipNegociacao negociacao={nota.tipoNegociacao} />;
  }

  function celulaOperacoes(nota: NotaBovespa | NotaBMF): JSX.Element {
    return (
      <Typography variant="dataMono">
        <InteiroText value={nota.quantidadeOperacoes} displayType={'text'} />
      </Typography>
    );
  }

  function celulaValorLiquido(nota: NotaBovespa | NotaBMF): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText displayType={'text'} value={nota.valorLiquido} />
      </Typography>
    );
  }

  function celulaUpload(nota: NotaBovespa | NotaBMF): JSX.Element {
    if (nota.upload)
      return (
        <Link
          to={`/arquivo/upload/${nota.upload}`}
          style={{ textDecoration: 'none' }}
        >
          <Typography
            variant="dataMono"
            sx={(theme) => ({
              // `accentInk`, não `primary.main`: accent sólido reprova em WCAG
              // como tinta sobre superfície escura.
              color: theme.palette.accentInk,
              '&:hover': { textDecoration: 'underline' },
            })}
          >
            {nota.upload}
          </Typography>
        </Link>
      );
    return (
      <Typography variant="labelMono" color="text.secondary">
        Manual
      </Typography>
    );
  }

  function celulaSituacao(nota: NotaBovespa | NotaBMF): JSX.Element {
    switch (nota.situacao) {
      case SituacaoNota.Nova:
        return <ChipStatus tom="accent" label="Lendo" />;
      case SituacaoNota.Obsoleta:
        return <ChipStatus tom="neutral" label="Obsoleta" />;
      case SituacaoNota.Pronta:
        return <ChipStatus tom="positive" label="Pronta" />;
      case SituacaoNota.Inconsistente:
        return <ChipStatus tom="destructive" label="Inconsistente" />;
      case SituacaoNota.Parcial:
        return <ChipStatus tom="warn" label="Parcial" />;
      default:
        return <></>;
    }
  }

  function celulaCorretora(nota: NotaBovespa | NotaBMF): JSX.Element {
    return <ChipCorretora codigoCorretora={nota.corretora} size="small" />;
  }

  function celulaDia(nota: NotaBovespa | NotaBMF): JSX.Element {
    return (
      <Typography variant="dataMono">
        {nota.dataPregao.format('DD/MM/YYYY')}
      </Typography>
    );
  }
}

/** Chip de status do sistema: cada estado da nota cai numa das cinco cores
 *  semânticas de `palette.statusChip` (DESIGN.md → Chips de status). */
function ChipStatus({
  label,
  tom,
}: {
  label: string;
  tom: 'positive' | 'accent' | 'warn' | 'destructive' | 'neutral';
}) {
  return (
    <Chip
      size="small"
      label={label}
      sx={(theme) => ({
        backgroundColor: theme.palette.statusChip[tom].bg,
        color: theme.palette.statusChip[tom].fg,
      })}
    />
  );
}
